import type { ReactNode } from 'react';
import { t } from '../i18n';
import type { DetailContext } from './types';
import { ChevronLeftIcon } from './icons';

/** Pill-style back link used at the top of the classroom and resource pages. */
export function BackLink({ ctx, label }: { ctx: DetailContext; label: string }) {
  return (
    <nav className="vp-breadcrumb">
      <a
        href={ctx.hrefs.back}
        className="vp-back-link"
        aria-label={ctx.locale === 'vi' ? 'Quay lại' : 'Back'}
      >
        <ChevronLeftIcon />
        {label}
      </a>
    </nav>
  );
}

/**
 * Report link. Renders nothing when the host supplies no report href, which is
 * how the admin preview drops it.
 */
export function ReportLink({ ctx, className = '' }: { ctx: DetailContext; className?: string }) {
  if (!ctx.hrefs.report) return null;
  return (
    <a
      className={`text-xs text-gray-400 hover:text-red-600 transition-colors${className ? ` ${className}` : ''}`}
      href={ctx.hrefs.report}
    >
      🚩 {t(ctx.locale, 'report_button')}
    </a>
  );
}

export function SectionLabel({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <span className="vp-section-label">
      {icon}
      {children}
    </span>
  );
}

/** One `<tr>` of the quick-summary table, skipped when it has nothing to show. */
export function SummaryRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <tr>
      <td>{label}</td>
      <td>{children}</td>
    </tr>
  );
}

export function Pill({ tone, children }: { tone: PillTone; children: ReactNode }) {
  return <span className={`vp-pill vp-pill-${tone}`}>{children}</span>;
}

export type PillTone = 'pink' | 'yellow' | 'mint' | 'blue';

/**
 * Floating contact bar that appears once the inline CTA row scrolls out of
 * view. Static markup — `attachDetailBehavior` toggles `vp-sticky-show`.
 */
export function StickyCta({ children }: { children: ReactNode }) {
  return (
    <div className="vp-sticky-cta" data-vp-sticky-cta aria-hidden="true">
      <div className="vp-sticky-cta-inner">{children}</div>
    </div>
  );
}
